import { useAuth0 } from '@auth0/auth0-react'

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { agenciesBrowseLink, hospitalsBrowseLink } from '../constants/directoryBrowseLinks'
import { useAuthToken } from '../providers/AuthStateContext'
import { ClaimedPage, ClaimedPagesService } from '../services/ClaimPages/ClaimedPagesService'
import { Spinner } from './Spinner'
import { StarRating } from './StarRating'

type PageType = 'hospital' | 'agency'
type TabType = 'all' | PageType

type ClaimedPageWithType = ClaimedPage & { pageType: PageType }

const isApprovedPage = (page: ClaimedPage): boolean => {
  const status = (page.claimStatus || '').toLowerCase()
  return status === 'approved' || status === 'claimed'
}

const getPageLink = (page: ClaimedPageWithType): string => {
  const permalink = page.original?.permalink
  if (page.pageType === 'hospital') {
    return permalink ? `/hospitals/${permalink}` : `/hospitals/${page.id}`
  }
  return permalink ? `/agencies/${permalink}` : `/agencies/${page.id}`
}

const getPageRating = (page: ClaimedPage): number => {
  return Number(page.original?.averageRating || 0)
}

const getReviewCount = (page: ClaimedPage): number => {
  return Number(page.original?.reviewCount || 0)
}

const getNewReviewsThisMonth = (page: ClaimedPage): number => {
  return Number(page.original?.newReviewsThisMonth || 0)
}

const getTabBorderColor = (pageType: PageType): string => {
  return pageType === 'hospital' ? 'border-aqua-mint' : 'border-orange'
}

const getTabBadgeClass = (pageType: PageType): string => {
  return pageType === 'hospital' ? 'bg-aqua-mint' : 'bg-orange'
}

const formatReviewSummary = (reviewCount: number, newThisMonth: number): string => {
  if (reviewCount === 0) return 'No reviews yet'
  const reviewLabel = `${reviewCount} review${reviewCount === 1 ? '' : 's'}`
  if (newThisMonth > 0) {
    return `${reviewLabel} · ${newThisMonth} new this month`
  }
  return reviewLabel
}

const hasDraftCustomization = (page: ClaimedPage): boolean =>
  page.customizationStatus === 'pending' || page.customizationStatus === 'not_proposed'

const getPageDisplayName = (page: ClaimedPage): string => {
  if (hasDraftCustomization(page) && page.customization?.name) {
    return page.customization.name
  }
  return page.name
}

const getPageDisplayLocation = (page: ClaimedPage): string => {
  if (hasDraftCustomization(page) && page.customization) {
    const city = page.customization.city ?? page.city
    const state = page.customization.state ?? page.state
    return [city, state].filter(Boolean).join(', ')
  }
  return [page.city, page.state].filter(Boolean).join(', ')
}

const getTabCountNoun = (tab: TabType, count: number): string => {
  if (tab === 'hospital') {
    return `${count} hospital${count === 1 ? '' : 's'}`
  }
  if (tab === 'agency') {
    return `${count} agenc${count === 1 ? 'y' : 'ies'}`
  }
  return `${count} page${count === 1 ? '' : 's'}`
}

const getTabButtonClass = (isActive: boolean): string =>
  `rounded-lg px-4 py-2 text-sm font-semibold transition-all ${
    isActive ? 'bg-orange text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
  }`

export const MyPagesList: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth0()
  const { isTokenReady } = useAuthToken()
  const [activeTab, setActiveTab] = useState<TabType>('all')
  const [hospitalPages, setHospitalPages] = useState<ClaimedPage[]>([])
  const [agencyPages, setAgencyPages] = useState<ClaimedPage[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    if (authLoading || !isAuthenticated || !isTokenReady) return

    let cancelled = false

    const load = async () => {
      setIsLoading(true)
      setError('')
      try {
        const service = new ClaimedPagesService()
        const [hospitals, agencies] = await Promise.all([service.getMyPages(false), service.getMyPages(true)])
        if (!cancelled) {
          setHospitalPages(hospitals)
          setAgencyPages(agencies)
        }
      } catch (err) {
        console.error('Failed to load my pages:', err)
        if (!cancelled) setError('Failed to load your pages. Please try again.')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [authLoading, isAuthenticated, isTokenReady])

  const approvedHospitalPages = useMemo(
    () => hospitalPages.filter(isApprovedPage).map((page) => ({ ...page, pageType: 'hospital' as const })),
    [hospitalPages],
  )
  const approvedAgencyPages = useMemo(
    () => agencyPages.filter(isApprovedPage).map((page) => ({ ...page, pageType: 'agency' as const })),
    [agencyPages],
  )
  const allApprovedPages = useMemo(
    () => [...approvedHospitalPages, ...approvedAgencyPages],
    [approvedHospitalPages, approvedAgencyPages],
  )

  const approvedPages = useMemo(() => {
    if (activeTab === 'all') return allApprovedPages
    if (activeTab === 'hospital') return approvedHospitalPages
    return approvedAgencyPages
  }, [activeTab, allApprovedPages, approvedHospitalPages, approvedAgencyPages])

  const hasAnyApprovedPages = allApprovedPages.length > 0

  const filteredPages = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return approvedPages

    return approvedPages.filter((page) => {
      const location = [page.city, page.state].filter(Boolean).join(', ').toLowerCase()
      return page.name.toLowerCase().includes(query) || location.includes(query) || String(page.id).includes(query)
    })
  }, [approvedPages, searchQuery])

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error) {
    return <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-600">{error}</div>
  }

  if (!hasAnyApprovedPages) {
    return (
      <div className="rounded-lg bg-white p-8 text-center shadow-lg md:p-12">
        <div className="mb-4 flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          </div>
        </div>
        <h3 className="mb-2 text-xl font-semibold text-graphite-grey">No pages yet</h3>
        <p className="mb-6 text-gray-500">
          You haven&apos;t claimed any pages yet. Claim a hospital or agency page to manage your organization&apos;s
          presence on ScrubStr.
        </p>
        <div className="flex flex-col justify-center gap-3 sm:flex-row">
          <Link
            to={hospitalsBrowseLink}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-aqua-mint px-6 py-3 font-semibold text-white shadow-md transition-all hover:bg-opacity-90"
          >
            Find a Hospital to Claim
          </Link>
          <Link
            to={agenciesBrowseLink}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-orange px-6 py-3 font-semibold text-white shadow-md transition-all hover:bg-opacity-90"
          >
            Find an Agency to Claim
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6 space-y-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search pages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 pl-10 focus:outline-none"
          />
          <svg
            className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>

        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setActiveTab('all')} className={getTabButtonClass(activeTab === 'all')}>
            All Pages
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('hospital')}
            className={getTabButtonClass(activeTab === 'hospital')}
          >
            Hospitals
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('agency')}
            className={getTabButtonClass(activeTab === 'agency')}
          >
            Agencies
          </button>
        </div>

        <div className="text-sm text-gray-600">
          {searchQuery.trim() && filteredPages.length !== approvedPages.length
            ? `Showing ${filteredPages.length} of ${getTabCountNoun(activeTab, approvedPages.length)}`
            : getTabCountNoun(activeTab, approvedPages.length)}
        </div>
      </div>

      {approvedPages.length === 0 ? (
        <div className="rounded-lg bg-white p-6 text-center shadow-lg md:p-8">
          <p className="mb-6 text-gray-600">
            No approved {activeTab === 'hospital' ? 'hospital' : 'agency'} pages yet.
          </p>
          <Link
            to={activeTab === 'agency' ? agenciesBrowseLink : hospitalsBrowseLink}
            className={`inline-flex items-center justify-center rounded-lg px-6 py-3 font-semibold text-white shadow-md transition-all hover:bg-opacity-90 ${activeTab === 'agency' ? 'bg-orange' : 'bg-aqua-mint'}`}
          >
            Claim another page
          </Link>
        </div>
      ) : filteredPages.length === 0 ? (
        <div className="rounded-lg bg-white p-6 text-center shadow-lg md:p-8">
          <p className="text-gray-600">No pages found matching your search criteria.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPages.map((page) => {
            const rating = getPageRating(page)
            const reviewCount = getReviewCount(page)
            const newThisMonth = getNewReviewsThisMonth(page)

            return (
              <div
                key={`${page.pageType}-${page.id}`}
                className={`rounded-lg border-2 bg-white p-6 shadow-md transition-all hover:shadow-lg ${getTabBorderColor(page.pageType)}`}
              >
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="mb-2 flex flex-wrap items-center gap-3">
                          <span className="text-base font-semibold text-graphite-grey">{getPageDisplayName(page)}</span>
                          <span
                            className={`rounded-full px-3 py-1 text-xs font-semibold capitalize text-white ${getTabBadgeClass(page.pageType)}`}
                          >
                            {page.pageType} Page
                          </span>
                        </div>
                        {getPageDisplayLocation(page) && (
                          <p className="text-sm font-medium text-gray-500">{getPageDisplayLocation(page)}</p>
                        )}
                      </div>

                      {rating > 0 && (
                        <div className="flex shrink-0 items-center gap-2">
                          <StarRating rating={rating} size="md" />
                          <span className="text-lg font-semibold text-graphite-grey">{rating.toFixed(1)}</span>
                        </div>
                      )}
                    </div>

                    <p className="mb-3 text-sm text-gray-600">{formatReviewSummary(reviewCount, newThisMonth)}</p>

                    <Link
                      to={getPageLink(page)}
                      className="inline-flex items-center gap-2 whitespace-nowrap rounded-xl border-2 border-aqua-mint/50 bg-white px-5 py-2.5 text-sm font-semibold text-aqua-mint shadow-sm transition-colors hover:border-aqua-mint hover:bg-aqua-mint/8"
                    >
                      View page
                    </Link>
                  </div>
                </div>
              </div>
            )
          })}

          <div className="flex flex-col gap-3 pt-2 sm:flex-row">
            <Link
              to={hospitalsBrowseLink}
              className="inline-flex items-center justify-center rounded-lg bg-aqua-mint px-6 py-3 font-semibold text-white shadow-md transition-all hover:bg-opacity-90"
            >
              Claim another hospital
            </Link>
            <Link
              to={agenciesBrowseLink}
              className="inline-flex items-center justify-center rounded-lg bg-orange px-6 py-3 font-semibold text-white shadow-md transition-all hover:bg-opacity-90"
            >
              Claim another agency
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
